import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../lib/cn";

export const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md transition-[background-color,color,border-color,scale] duration-140 ease-emphasized focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--focus-ring,var(--color-primary))] active:scale-[0.98] disabled:pointer-events-none disabled:cursor-not-allowed disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary-button-background text-primary-button-text hover:bg-blue-purple-600 disabled:bg-primary-button-disabled [&_svg]:text-primary-button-icon",
        secondary:
          "border border-secondary-button-outline bg-secondary-button-background text-secondary-button-text hover:bg-container-hover disabled:bg-secondary-button-disabled [&_svg]:text-secondary-button-icon",
      },
      size: {
        sm: "h-8 px-3 text-body-s-b gap-1.5 [&_svg]:size-3.5",
        md: "h-10 px-4 text-body-m-b gap-2 [&_svg]:size-4",
        lg: "h-12 px-6 text-body-l-b gap-2.5 [&_svg]:size-5",
        // A list screen's header actions (Archive, History, Create): one
        // shared minimum width so they line up, and the icon always on the
        // right of the label -- in Arabic too, so RTL reverses the row.
        toolbar:
          "h-10 min-w-[98px] px-4 text-body-s-r gap-2 rtl:flex-row-reverse lg:min-w-[123px] [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
